import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, ShieldCheck, FileSearch, MessagesSquare, ArrowUpRight, Scale, Activity, Lock } from 'lucide-react';

const ImmigrationSolicitors: React.FC = () => {
    const navigate = useNavigate();

    const handleRunAI = () => {
        navigate('/eligibility?service=immigration');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20 font-sans">
            {/* Hero Header */}
            <div className="bg-gradient-to-r from-[#011E26] via-[#011E26] to-[#011E26] text-white py-24 mb-16 relative overflow-hidden border-b border-[#009B8F]/30">
                <div className="absolute bottom-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
                    <Scale className="w-full h-full -rotate-12 translate-x-1/4 translate-y-1/4" />
                </div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-12 h-0.5 bg-[#009B8F]"></div>
                        <span className="text-[#009B8F] font-bold tracking-[0.3em] uppercase text-xs">Expert Escalation</span>
                    </div>
                    <h1 className="text-5xl md:text-8xl font-black mb-10 tracking-tighter leading-none">
                        Connect with <br /> <span className="text-[#009B8F]">Solicitors</span>
                    </h1>
                    <p className="text-xl md:text-2xl text-slate-400 max-w-3xl font-light leading-relaxed mb-12">
                        Escalate your case safely with regulated independent professionals for high-risk profiles
                        or complex immigration histories.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                {/* Core Value Prop */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32">
                    <div className="space-y-8">
                        <h2 className="text-4xl font-bold text-[#011E26] leading-tight">
                            A Secure <span className="text-[#009B8F]">Bridge</span> to Legal Expertise.
                        </h2>
                        <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
                            <p>
                                While NexOverseas provides powerful automation tools, certain situations such as
                                previous refusals or complex histories may require professional interpretation.
                            </p>
                            <p>
                                This feature ensures you can escalate your case safely, quickly, and with complete
                                confidence. We act as a bridge between you and independent, regulated experts.
                            </p>
                        </div>
                        <div className="flex items-center gap-4 p-6 bg-slate-50 rounded-3xl border border-slate-100">
                            <Activity className="text-[#009B8F] flex-shrink-0" />
                            <p className="text-sm font-bold text-[#011E26]">UK-Bound Students: <span className="text-[#009B8F]">Facilities Currently Available</span></p>
                        </div>
                    </div>
                    <div className="relative group">
                        <div className="absolute -inset-4 bg-gradient-to-r from-[#009B8F] to-[#011E26] rounded-[3.5rem] blur opacity-10 group-hover:opacity-20 transition duration-1000"></div>
                        <img
                            src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070&auto=format&fit=crop"
                            alt="Professional Solicitor"
                            className="relative rounded-[3rem] shadow-2xl border border-slate-100"
                        />
                    </div>
                </div>

                {/* Workflow Section */}
                <div className="mb-32">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-bold text-[#011E26] mb-4 text-center">How It Works</h2>
                        <p className="text-slate-500 text-lg">A seamless handover from AI insights to human expertise.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <AlertUpRight className="w-8 h-8 text-[#009B8F]" />,
                                title: "Anomaly Trigger",
                                desc: "If high-risk data points are identified, the system suggests professional review. The choice is yours."
                            },
                            {
                                icon: <Lock className="w-8 h-8 text-[#009B8F]" />,
                                title: "Secure Transfer",
                                desc: "With your consent, relevant profile info is shared securely with the solicitor for efficient handover."
                            },
                            {
                                icon: <ShieldCheck className="w-8 h-8 text-[#009B8F]" />,
                                title: "Independent Review",
                                desc: "A regulated immigration solicitor reviews your case and provides personalised advice directly."
                            },
                            {
                                icon: <FileSearch className="w-8 h-8 text-[#009B8F]" />,
                                title: "AI-Assisted Pre-Check",
                                desc: "System runs an automated summary of your data for the solicitor to highlight key areas."
                            },
                            {
                                icon: <MessagesSquare className="w-8 h-8 text-[#009B8F]" />,
                                title: "Integrated Tracking",
                                desc: "Monitor the progress of your legal referral directly through your NexOverseas dashboard."
                            },
                            {
                                icon: <Briefcase className="w-8 h-8 text-[#009B8F]" />,
                                title: "Professional Advice",
                                desc: "Receive expert guidance authorised to advise on complex immigration matters."
                            }
                        ].map((card, i) => (
                            <div key={i} className="p-10 rounded-3xl bg-slate-50 border border-slate-100 hover:border-[#009B8F] hover:bg-white hover:shadow-2xl transition-all group">
                                <div className="mb-6 group-hover:scale-110 transition-transform">{card.icon}</div>
                                <h3 className="text-xl font-bold text-[#011E26] mb-4">{card.title}</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">{card.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Disclaimer & Action */}
                <div className="bg-[#009B8F] rounded-[4rem] p-12 md:p-24 shadow-2xl relative overflow-hidden text-center group">
                    <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10">
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-10 tracking-tight">Escalate with Confidence</h2>
                        <p className="text-[#011E26] text-xl font-medium max-w-2xl mx-auto mb-12">
                            Let our AI run a preliminary check. If your profile needs expert eyes, we'll provide
                            the connection.
                        </p>
                        <button
                            onClick={handleRunAI}
                            className="bg-[#011E26] text-white px-12 py-6 rounded-3xl font-bold text-xl hover:bg-white hover:text-[#011E26] transition-all flex items-center justify-center gap-4 mx-auto shadow-2xl"
                        >
                            Run AI Check
                            <ArrowUpRight className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <div className="max-w-2xl mx-auto text-center mt-12 space-y-4">
                    <p className="text-slate-400 text-xs italic">
                        Disclaimer: This feature connects users to independent, regulated legal professionals.
                        NexOverseas does not provide legal advice. All guidance is delivered solely by the
                        solicitor you engage.
                    </p>
                </div>
            </div>
        </div>
    );
};

// Custom helper icon since ArrowUpRight is different
function AlertUpRight({ className }: { className?: string }) {
    return (
        <div className={className}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M17 7H7M17 7V17" />
            </svg>
        </div>
    );
}

export default ImmigrationSolicitors;
